import {
  BuiltInVariableType,
  CloudFlowNodeType,
  type ReferencedNodeValue,
  type RelativeTimeDiffValue,
  TimeFrequency,
} from "@doitintl/cmp-models";
import { z } from "zod";

import { type NodeModelWithId } from "./types";

const relativeTimeDiffValueSchema = z.object({
  builtInVariable: z.literal(BuiltInVariableType.RELATIVE_TIME_DIFF),
  diff: z.number(),
  unit: z.nativeEnum(TimeFrequency),
});

export function isRelativeTimeDiffValue(value: unknown): value is RelativeTimeDiffValue {
  return relativeTimeDiffValueSchema.safeParse(value).success;
}

export function isVariableValue(value: unknown): value is ReferencedNodeValue {
  return isReferencedNodeValue(value) && value.referencedField[0] === "variables";
}

export function isReferencedNodeValue(value: unknown): value is ReferencedNodeValue {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const keys = Object.keys(value);
  const expectedKeys = ["referencedNodeId", "referencedField"];

  return keys.length === expectedKeys.length && expectedKeys.every((expectedKey) => expectedKey in value);
}

export function areReferencedNodeValuesEqual(
  a: ReferencedNodeValue | null | undefined,
  b: ReferencedNodeValue | null | undefined
): boolean {
  return (
    isReferencedNodeValue(a) &&
    isReferencedNodeValue(b) &&
    a.referencedNodeId === b.referencedNodeId &&
    a.referencedField.length === b.referencedField.length &&
    a.referencedField.every((token, idx) => token === b.referencedField[idx])
  );
}

export function isTriggerNode(
  nodeEntity: NodeModelWithId
): nodeEntity is NodeModelWithId<
  | CloudFlowNodeType.TRIGGER
  | CloudFlowNodeType.MANUAL_TRIGGER
  | CloudFlowNodeType.WEBHOOK_TRIGGER
  | CloudFlowNodeType.SYSTEM_EVENT_TRIGGER
> {
  return [
    CloudFlowNodeType.MANUAL_TRIGGER,
    CloudFlowNodeType.TRIGGER,
    CloudFlowNodeType.WEBHOOK_TRIGGER,
    CloudFlowNodeType.SYSTEM_EVENT_TRIGGER,
  ].includes(nodeEntity.type);
}

export function isScheduleTriggerNode(
  nodeEntity: NodeModelWithId
): nodeEntity is NodeModelWithId<CloudFlowNodeType.TRIGGER> {
  return nodeEntity.type === CloudFlowNodeType.TRIGGER;
}

export function isManualTriggerNode(
  nodeEntity: NodeModelWithId
): nodeEntity is NodeModelWithId<CloudFlowNodeType.MANUAL_TRIGGER> {
  return nodeEntity.type === CloudFlowNodeType.MANUAL_TRIGGER;
}

export function isWebhookTriggerNode(
  nodeEntity: NodeModelWithId
): nodeEntity is NodeModelWithId<CloudFlowNodeType.WEBHOOK_TRIGGER> {
  return nodeEntity.type === CloudFlowNodeType.WEBHOOK_TRIGGER;
}

export function isSystemEventTriggerNode(
  nodeEntity: NodeModelWithId
): nodeEntity is NodeModelWithId<CloudFlowNodeType.SYSTEM_EVENT_TRIGGER> {
  return nodeEntity.type === CloudFlowNodeType.SYSTEM_EVENT_TRIGGER;
}

export function isActionNode(nodeEntity: NodeModelWithId): nodeEntity is NodeModelWithId<CloudFlowNodeType.ACTION> {
  return nodeEntity.type === CloudFlowNodeType.ACTION;
}

export function isFilterNode(nodeEntity: NodeModelWithId): nodeEntity is NodeModelWithId<CloudFlowNodeType.FILTER> {
  return nodeEntity.type === CloudFlowNodeType.FILTER;
}

export function isConditionNode(
  nodeEntity: NodeModelWithId
): nodeEntity is NodeModelWithId<CloudFlowNodeType.CONDITION> {
  return nodeEntity.type === CloudFlowNodeType.CONDITION;
}

export function isTransformationNode(
  nodeEntity: NodeModelWithId
): nodeEntity is NodeModelWithId<CloudFlowNodeType.TRANSFORMATION> {
  return nodeEntity.type === CloudFlowNodeType.TRANSFORMATION;
}

export function isDateTransformationNode(
  nodeEntity: NodeModelWithId
): nodeEntity is NodeModelWithId<CloudFlowNodeType.DATETIME_TRANSFORM> {
  return nodeEntity.type === CloudFlowNodeType.DATETIME_TRANSFORM;
}
